import {
  ReactElement,
  ReactNode,
  isValidElement,
  cloneElement,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { Paper, Stack, Typography } from '@mui/material';
import { TooltipProps } from 'recharts';

type GenericTooltipProps = TooltipProps<number, string>;

type TooltipContent =
  | ((props: TooltipContentProps) => ReactElement)
  | ReactElement<TooltipContentProps>;

const TOOLTIP_HORIZONTAL_OFFSET = 24;

export type TooltipContentProps = {
  title?: string;
  valueFormatter?: (name: number) => string;
  legendFormatter?: (name: string) => string;
  payload?: GenericTooltipProps['payload'];
};

const renderContent = (
  content: TooltipContent | undefined,
  props: TooltipContentProps,
): ReactNode => {
  if (typeof content === 'function') {
    return content(props);
  }

  if (isValidElement(content)) {
    return cloneElement(content, props);
  }

  return props.payload ? (
    <>
      {props.title && (
        <Typography variant='subtitle2' mb={1} noWrap>
          {props.title}
        </Typography>
      )}
      {props.payload.map((item, index) => (
        <Stack
          key={index}
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          spacing={3}
        >
          {props.legendFormatter && item.name && item.name !== 'value' ? (
            <Typography variant='caption' color='text.secondary'>
              {props.legendFormatter(item.name)}
            </Typography>
          ) : null}
          <Typography variant='subtitle2' color={item.color}>
            {item.value && props.valueFormatter ? props.valueFormatter(item.value) : item.value}
          </Typography>
        </Stack>
      ))}
    </>
  ) : null;
};

export type MetricChartTooltipProps = GenericTooltipProps & {
  tooltipContent?: TooltipContent;
  valueFormatter?: (name: number) => string;
  ticksFormatter?: (name: number) => string;
  legendFormatter?: (name: string) => string;
};

export const MetricChartTooltip = (props: MetricChartTooltipProps) => {
  const {
    active,
    coordinate,
    payload,
    tooltipContent,
    label,
    ticksFormatter,
    legendFormatter,
    valueFormatter,
    viewBox,
  } = props;

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      setTooltipSize({
        width: tooltipRef.current.offsetWidth,
        height: tooltipRef.current.offsetHeight,
      });
    }
  }, [payload, label, tooltipContent]);

  if (!active || !coordinate || !payload || !payload.length) {
    return null;
  }

  let tooltipX = coordinate.x ?? 0;
  let tooltipY = (coordinate.y ?? 0) - tooltipSize.height / 2;
  const chartLeft = viewBox?.x ?? 0;
  const chartTop = viewBox?.y ?? 0;
  const chartRight = chartLeft + (viewBox?.width ?? 0);
  const chartBottom = chartTop + (viewBox?.height ?? 0);

  const showTooltipFromLeftSide =
    tooltipX + tooltipSize.width + TOOLTIP_HORIZONTAL_OFFSET > chartRight;

  tooltipX = showTooltipFromLeftSide
    ? tooltipX - tooltipSize.width - TOOLTIP_HORIZONTAL_OFFSET
    : tooltipX + TOOLTIP_HORIZONTAL_OFFSET;

  if (tooltipY < chartTop) {
    tooltipY = chartTop;
  } else if (tooltipY + tooltipSize.height > chartBottom) {
    tooltipY = chartBottom - tooltipSize.height;
  }

  return (
    <Paper
      ref={tooltipRef}
      elevation={4}
      sx={(theme) => ({
        p: 2,
        position: 'absolute',
        left: tooltipX,
        top: tooltipY,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          borderTop: `${theme.spacing(1)} solid transparent`,
          borderBottom: `${theme.spacing(1)} solid transparent`,
          ...(showTooltipFromLeftSide
            ? {
                right: `-${theme.spacing(1)}`,
                borderLeft: `${theme.spacing(1)} solid ${theme.palette.background.paper}`,
                filter: 'drop-shadow(1px 0 0 rgba(0,0,0,0.3))',
              }
            : {
                left: `-${theme.spacing(1)}`,
                borderRight: `${theme.spacing(1)} solid ${theme.palette.background.paper}`,
                filter: 'drop-shadow(-1px 0 0 rgba(0,0,0,0.3))',
              }),
        },
      })}
    >
      {renderContent(tooltipContent, {
        title: ticksFormatter ? ticksFormatter(Number(label)) : undefined,
        legendFormatter,
        valueFormatter,
        payload,
      })}
    </Paper>
  );
};
